import { useCallback, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { AppCard, AppModal, AppRemixIcon } from '@/layout/AppComponents';
import { ResponsivePie } from '@nivo/pie';
import { UnmountClosed } from 'react-collapse';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import ResourceExpandedContainer from '../ExpandedTabs/Resource/ResourceExpandedContainer';
import IconListBlock from '../CardElements/IconListBlock';
import { getState } from '../tools/resourceManagerTools';
import colors from '../../../../../constants/colors';
import Translator from '../../../../components/helpers/Translator';
import ImageCropper from '../../../../../layout/components/form/ImageCropper';
import { axiosPath } from '../../../../../routing/helpers/router';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import AppSpinner from '../../../../components/AppSpinner';
import _ from 'lodash';

export default function ResourceDefault({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const resLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1
  );
  const resLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2
  );
  const updateCardTrigger = useSelector(
    (state) => state.currentPage?.updateCard
  );
  const [isOpened, setIsOpened] = useState(false);
  const [showClientImageModal, setShowClientImageModal] = useState(false);
  const handleOnSaveResourceImage = useCallback(
    async (resourceImage) => {
      axios
        .post(
          axiosPath("api_r_user_module_resourceManager_resource_image_set"),
          {
            moduleBridgeHex,
            resourceID: resLevel1[elementID]?.id,
            resourceImage,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            const updatedResLevel1 = { ...resLevel1 };
            updatedResLevel1[elementID].image = data?.image;
            dispatch(
              currentPageAction.change("elementLevels", {
                level1: { ...updatedResLevel1 },
              })
            );
            setShowClientImageModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, elementID, resLevel1]
  );
  if (!resLevel1) {
    return (
      <div className="card po-default">
        <div className="card-body" style={{ minHeight: '112px' }} />
      </div>
    );
  }

  return (
    <Container
      key={elementID}
      className="mb-1"
      $state={getState(resLevel1[elementID]?.state)?.bgColor}
    >
      <AppCard
        bodyClassName="p-0"
        headerClassName="p-0"
        header={
          <HeaderContainer>
            <div className="category-header">
              {`${resLevel1[elementID]?.category || ''} / ${
                resLevel1[elementID]?.subcategory || ''
              }`}
            </div>
            {updateCardTrigger === elementID || !resLevel1 || !resLevel2 ? (
              <AppSpinner>
                <AppRemixIcon
                  className="loading-icon"
                  icon="loader-4"
                  color={colors.primary}
                  size={22}
                />
              </AppSpinner>
            ) : (
              <span className="loading-icon" />
            )}
          </HeaderContainer>
        }
      >
        <ResourceContainer
          $state={getState(resLevel1[elementID]?.state)?.bgColor}
        >
          <div className="default-content">
            <div
              className="rs-status"
              title={getState(resLevel1[elementID]?.state)?.title}
            >
              <AppRemixIcon
                icon={getState(resLevel1[elementID]?.state)?.icon}
                color={getState(resLevel1[elementID]?.state)?.fgColor}
                size={24}
              />
            </div>
            <div className="content">
              <img
                className={`rs-image ${
                  userBridge?.permissions?.level === 0 ? 'pointer' : ''
                }`}
                alt={resLevel1[elementID]?.name}
                src={
                  resLevel1[elementID]?.image ??
                  resLevel1[elementID]?.categoryImage ??
                  'https://worldlex.net/assets/images/wllg.jpg'
                }
                onClick={() =>
                  userBridge?.permissions?.level === 0 &&
                  setShowClientImageModal(true)
                }
              />
              <div className="rs-description">
                <p>{resLevel1[elementID]?.name}</p>
              </div>
              <div
                className={`rs-unfulfilled${
                  resLevel2?.[elementID]?.unfulfilled === null ? ' empty' : ''
                }`}
              >
                {resLevel2?.[elementID]?.unfulfilled?.length > 0 &&
                  resLevel2[elementID]?.unfulfilled.map((p) => (
                    <div
                      key={p?.obligationHex}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-resourceManager"
                          name="resource.obligations-code"
                          variables={{
                            code: p?.obligationHex,
                          }}
                        />,
                      )}
                    >
                      <AppRemixIcon
                        icon="calendar-close"
                        color={getState(p?.state)?.bgColor}
                        className="mr-2"
                      />
                      <span
                        key={p?.id}
                        style={{ color: getState(p?.state)?.bgColor }}
                      >
                        {p.dateRange}
                      </span>
                    </div>
                  ))}
                {resLevel2?.[elementID]?.unfulfilled === null && (
                  <div>
                    {' '}
                    <Translator
                      file="module-resourceManager"
                      name="resource.no-pending-dates"
                    />
                  </div>
                )}
              </div>
              <div className="rs-state">
                <ChartContainer>
                  {!_.isEmpty(resLevel2?.[elementID]?.chartData) ? (
                    <ResponsivePie
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      data={resLevel2[elementID]?.chartData}
                      padAngle={1}
                      innerRadius={0.4}
                      cornerRadius={2}
                      colors={(d) => d?.data?.color}
                      enableArcLinkLabels={false}
                      enableArcLabels={false}
                    />
                  ) : (
                    <div className='chart-no-info'>
                      <AppRemixIcon
                        icon="eye-off"
                        color={colors.grey500}
                        size={28}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="general.no-elements"
                          />,
                        )}
                      />
                    </div>
                  )}
                </ChartContainer>
              </div>
              <IconListBlock elementID={elementID} />
            </div>
            <div
              className="rs-init-end bg-primary pointer"
              onClick={() => setIsOpened(!isOpened)}
            >
              <AppRemixIcon
                icon={isOpened ? 'subtract' : 'add'}
                color="white"
              />
            </div>
          </div>
        </ResourceContainer>
      </AppCard>
      <UnmountClosed isOpened={isOpened}>
        <ResourceExpandedContainer elementID={elementID} />
      </UnmountClosed>
      <AppModal
        show={showClientImageModal}
        size="lg"
        isConfirmBtn={false}
        isCancelBtn
        confirmBtnText={ReactDOMServer.renderToString(
          <Translator file="module-resourceManager" name="resource.confirm" />,
        )}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-resourceManager"
            name="resource.change.asset.image"
          />,
        )}
        onClose={() => setShowClientImageModal(false)}
      >
        <>
          <p>
            <Translator
              file="client"
              name="general.image-size-not-used-recommended-upload"
              isHtml
            />
            <a href="https://www.photopea.com/">
              <Translator file="client" name="general.photopea-free-online" />
            </a>
            ,<a href="https://www.canva.com/es_es/">Canva</a>, PowerPoint, etc.
          </p>
          <ImageCropper
            cropShape="rect"
            image={resLevel1[elementID]?.image ?? null}
            width={300}
            height={300}
            onSave={handleOnSaveResourceImage}
          />
        </>
      </AppModal>
    </Container>
  );
}

const HeaderContainer = styled.div`
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .loading-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 22px;
    height: 22px;
  }
  .category-header {
    font-size: 13px;
    color: rgba(5, 85, 98, 0.52);
    height: 22px;
  }
`;
const Container = styled.div`
  .card {
    border-radius: 5px;
    border: 1px solid ${(props) => props.$state};
  }
`;
const ResourceContainer = styled.div`
  height: 80px;
  .rs-image {
    width: 80px;
    height: 80px;
    background-color: ${colors.grey400};
    color: ${colors.grey400};
    font-size: 10px;
  }
  .default-content {
    display: grid;
    grid-template-columns: 40px 1fr 40px;

    /* overflow: hidden; */
    .rs-status {
      display: flex;
      width: 40px;
      height: 100%;
      background-color: ${(props) => props.$state};
      align-items: center;
      justify-content: center;
    }
    .content {
      display: grid;
      grid-template-columns: 80px 1fr 240px 80px 56px;

      img {
        width: 80px;
        height: 80px;
        overflow: hidden;
      }
      .rs-description {
        border-right: 1px solid #ccc;
        padding: 8px;
        overflow-y: auto;
        height: 80px;
      }
      .rs-unfulfilled {
        border-right: 1px solid #ccc;
        padding: 8px;
        overflow-y: auto;
        height: 80px;
        &.empty {
          display: flex;
          justify-content: center;
          align-items: center;
          color: ${colors.grey500};
        }
      }
      .rs-state {
        display: flex;
        justify-content: center;
        align-items: center;
        border-right: 1px solid #ccc;
      }
    }
    & > .rs-init-end {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 2px;
      font-size: 20px;
      height: 76px;
      margin: 2px;
    }
  }
`;

const ChartContainer = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  .chart-no-info {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
